import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { LocalizedProcessingBadge, useT, type Translations } from '@/shared/i18n'
import { ToolHero } from '@/shared/ui/ToolHero'

type OutputMime = 'image/jpeg' | 'image/png' | 'image/webp'
type UpscalePresetKey = '2' | '3' | '4' | 'custom'
type UpscaleErrors = Translations['upscale']['errorsText']

interface LoadedImage {
  file: File
  url: string
  width: number
  height: number
  mime: OutputMime
}

interface UpscaleResult {
  blob: Blob
  url: string
  width: number
  height: number
  mime: OutputMime
}

const UTILITY_KEY = 'upscale-image'
const MAX_FILE_SIZE = 50 * 1024 * 1024
const MAX_INPUT_PIXELS = 36_000_000
const MAX_SCALE = 4
const MAX_OUTPUT_PIXELS = 64_000_000
const ALLOWED_TYPES: OutputMime[] = ['image/jpeg', 'image/png', 'image/webp']

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

function clampDimension(value: number, max: number): number {
  if (!Number.isFinite(value)) {
    return 1
  }
  return clamp(Math.round(value), 1, Math.max(1, max))
}

function fileMime(file: File): OutputMime | null {
  const mime = file.type.toLowerCase()
  if (ALLOWED_TYPES.includes(mime as OutputMime)) {
    return mime as OutputMime
  }

  const name = file.name.toLowerCase()
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image/jpeg'
  if (name.endsWith('.png')) return 'image/png'
  if (name.endsWith('.webp')) return 'image/webp'
  return null
}

function loadImage(src: string, errors: UpscaleErrors): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error(errors.corrupt))
    img.src = src
  })
}

function blobFromCanvas(
  canvas: HTMLCanvasElement,
  mime: OutputMime,
  quality: number,
  errors: UpscaleErrors,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error(errors.upscaleFailed))
          return
        }
        if (mime === 'image/webp' && blob.type !== 'image/webp') {
          reject(new Error(errors.unsupportedOutput))
          return
        }
        resolve(blob)
      },
      mime,
      mime === 'image/png' ? undefined : quality / 100,
    )
  })
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

function outputFilename(file: File, mime: OutputMime): string {
  const base = file.name.replace(/\.[^.]+$/, '') || 'image'
  const ext = mime === 'image/jpeg' ? 'jpg' : mime.replace('image/', '')
  return `${base}-upscaled.${ext}`
}

function sizeDelta(original: number, result: number): number {
  if (original <= 0) return 0
  return Math.round(((result - original) / original) * 100)
}

function defaultTargetDimensions(width: number, height: number): {
  width: number
  height: number
  preset: UpscalePresetKey
} {
  const twoXWidth = width * 2
  const twoXHeight = height * 2
  if (twoXWidth * twoXHeight <= MAX_OUTPUT_PIXELS) {
    return { width: twoXWidth, height: twoXHeight, preset: '2' }
  }

  const scale = Math.sqrt(MAX_OUTPUT_PIXELS / (width * height))
  return {
    width: Math.max(width, Math.floor(width * scale)),
    height: Math.max(height, Math.floor(height * scale)),
    preset: 'custom',
  }
}

export function UpscaleImagePage() {
  const t = useT()
  const [image, setImage] = useState<LoadedImage | null>(null)
  const [targetWidth, setTargetWidth] = useState(1)
  const [targetHeight, setTargetHeight] = useState(1)
  const [keepAspect, setKeepAspect] = useState(true)
  const [preset, setPreset] = useState<UpscalePresetKey>('2')
  const [quality, setQuality] = useState(92)
  const [result, setResult] = useState<UpscaleResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [dropActive, setDropActive] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const clearResult = useCallback(() => {
    setResult(null)
  }, [])

  useEffect(() => {
    return () => {
      if (image) {
        URL.revokeObjectURL(image.url)
      }
    }
  }, [image])

  useEffect(() => {
    return () => {
      if (result) {
        URL.revokeObjectURL(result.url)
      }
    }
  }, [result])

  const aspectRatio = image ? image.width / image.height : 1
  const maxWidth = image ? image.width * MAX_SCALE : 1
  const maxHeight = image ? image.height * MAX_SCALE : 1
  const outputPixels = targetWidth * targetHeight
  const canUpscale =
    image !== null &&
    !isProcessing &&
    targetWidth >= 1 &&
    targetHeight >= 1 &&
    outputPixels <= MAX_OUTPUT_PIXELS
  const resultDelta = result ? sizeDelta(image?.file.size ?? 0, result.blob.size) : null
  const scaleLabel = image
    ? `${(targetWidth / image.width).toFixed(2)}x`
    : '2x'

  const applyDimensions = useCallback(
    (width: number, height: number, nextPreset: UpscalePresetKey) => {
      if (!image) return
      const nextWidth = clampDimension(width, maxWidth)
      const nextHeight = clampDimension(height, maxHeight)
      setTargetWidth(nextWidth)
      setTargetHeight(nextHeight)
      setPreset(nextPreset)
      setError(
        nextWidth * nextHeight > MAX_OUTPUT_PIXELS
          ? t.upscale.errorsText.tooLargeOutput
          : null,
      )
      clearResult()
    },
    [clearResult, image, maxHeight, maxWidth, t.upscale.errorsText.tooLargeOutput],
  )

  const applyPreset = useCallback(
    (nextPreset: Exclude<UpscalePresetKey, 'custom'>) => {
      if (!image) return
      const multiplier = Number(nextPreset)
      applyDimensions(
        image.width * multiplier,
        image.height * multiplier,
        nextPreset,
      )
    },
    [applyDimensions, image],
  )

  const setLoadedImage = useCallback(
    async (file: File) => {
      setError(null)
      clearResult()

      const mime = fileMime(file)
      if (!mime) {
        setError(t.upscale.errorsText.unsupported)
        toolError(UTILITY_KEY, 'unsupported_type')
        return
      }
      if (file.size > MAX_FILE_SIZE) {
        setError(t.upscale.errorsText.tooLarge)
        toolError(UTILITY_KEY, 'too_large')
        return
      }

      const url = URL.createObjectURL(file)
      try {
        const decoded = await loadImage(url, t.upscale.errorsText)
        const width = decoded.naturalWidth
        const height = decoded.naturalHeight
        if (width < 1 || height < 1 || width * height > MAX_INPUT_PIXELS) {
          throw new Error(t.upscale.errorsText.tooLargeImage)
        }

        const defaultTarget = defaultTargetDimensions(width, height)
        const nextImage: LoadedImage = { file, url, width, height, mime }
        setImage((previous) => {
          if (previous) {
            URL.revokeObjectURL(previous.url)
          }
          return nextImage
        })
        setKeepAspect(true)
        setQuality(92)
        setPreset(defaultTarget.preset)
        setTargetWidth(defaultTarget.width)
        setTargetHeight(defaultTarget.height)
      } catch (err) {
        URL.revokeObjectURL(url)
        const message = err instanceof Error ? err.message : t.upscale.errorsText.corrupt
        setError(message)
        toolError(
          UTILITY_KEY,
          message === t.upscale.errorsText.tooLargeImage
            ? 'too_large_image'
            : message === t.upscale.errorsText.tooLargeOutput
              ? 'too_large_output'
              : 'corrupt_image',
        )
      }
    },
    [clearResult, t.upscale.errorsText],
  )

  const addFiles = useCallback(
    (incoming: FileList | File[]) => {
      const [file] = Array.from(incoming)
      if (file) {
        void setLoadedImage(file)
      }
    },
    [setLoadedImage],
  )

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files.length > 0) {
        addFiles(event.target.files)
        event.target.value = ''
      }
    },
    [addFiles],
  )

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setDropActive(true)
  }, [])

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setDropActive(false)
  }, [])

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      event.stopPropagation()
      setDropActive(false)
      if (event.dataTransfer.files.length > 0) {
        addFiles(event.dataTransfer.files)
      }
    },
    [addFiles],
  )

  const handleWidthChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!image) return
      const width = clampDimension(Number(event.target.value), maxWidth)
      const height = keepAspect ? clampDimension(width / aspectRatio, maxHeight) : targetHeight
      applyDimensions(width, height, 'custom')
    },
    [applyDimensions, aspectRatio, image, keepAspect, maxHeight, maxWidth, targetHeight],
  )

  const handleHeightChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!image) return
      const height = clampDimension(Number(event.target.value), maxHeight)
      const width = keepAspect ? clampDimension(height * aspectRatio, maxWidth) : targetWidth
      applyDimensions(width, height, 'custom')
    },
    [applyDimensions, aspectRatio, image, keepAspect, maxHeight, maxWidth, targetWidth],
  )

  const handleKeepAspectChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!image) return
      const enabled = event.target.checked
      setKeepAspect(enabled)
      setPreset('custom')
      setError(null)
      clearResult()
      if (enabled) {
        setTargetHeight(clampDimension(targetWidth / aspectRatio, maxHeight))
      }
    },
    [aspectRatio, clearResult, image, maxHeight, targetWidth],
  )

  const handleQualityChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setQuality(Number(event.target.value))
      setError(null)
      clearResult()
    },
    [clearResult],
  )

  const upscaleToBlob = useCallback(async (): Promise<Blob> => {
    if (!image) {
      throw new Error(t.upscale.errorsText.noImage)
    }
    if (targetWidth * targetHeight > MAX_OUTPUT_PIXELS) {
      throw new Error(t.upscale.errorsText.tooLargeOutput)
    }

    const decoded = await loadImage(image.url, t.upscale.errorsText)
    const canvas = document.createElement('canvas')
    canvas.width = targetWidth
    canvas.height = targetHeight

    const context = canvas.getContext('2d')
    if (!context) {
      throw new Error(t.upscale.errorsText.canvas)
    }

    if (image.mime === 'image/jpeg') {
      context.fillStyle = '#FFFFFF'
      context.fillRect(0, 0, canvas.width, canvas.height)
    }

    context.imageSmoothingEnabled = true
    context.imageSmoothingQuality = 'high'
    context.drawImage(decoded, 0, 0, targetWidth, targetHeight)

    return blobFromCanvas(canvas, image.mime, quality, t.upscale.errorsText)
  }, [image, quality, t.upscale.errorsText, targetHeight, targetWidth])

  const handleUpscale = useCallback(async () => {
    if (!image) return

    const startTime = performance.now()
    toolInvoke(UTILITY_KEY, {
      preset,
      width: targetWidth,
      height: targetHeight,
      scale: scaleLabel,
      method: 'canvas',
    })
    setIsProcessing(true)
    setError(null)
    clearResult()

    try {
      const blob = await upscaleToBlob()
      const url = URL.createObjectURL(blob)
      setResult({
        blob,
        url,
        width: targetWidth,
        height: targetHeight,
        mime: image.mime,
      })
      toolSuccess(UTILITY_KEY, Math.round(performance.now() - startTime), {
        preset,
        width: targetWidth,
        height: targetHeight,
        mime: image.mime,
        scale: scaleLabel,
        method: 'canvas',
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : t.upscale.errorsText.upscaleFailed)
      toolError(UTILITY_KEY, 'upscale_failed', {
        preset,
        width: targetWidth,
        height: targetHeight,
        scale: scaleLabel,
        method: 'canvas',
      })
    } finally {
      setIsProcessing(false)
    }
  }, [
    clearResult,
    image,
    preset,
    scaleLabel,
    t.upscale.errorsText.upscaleFailed,
    targetHeight,
    targetWidth,
    upscaleToBlob,
  ])

  const handleDownload = useCallback(() => {
    if (!image || !result) return
    downloadBlob(result.blob, outputFilename(image.file, result.mime))
  }, [image, result])

  return (
    <div className="mx-auto max-w-6xl">
      <ToolHero
        eyebrow={t.upscale.page.eyebrow}
        title={t.upscale.page.title}
        description={t.upscale.page.description}
        chips={[t.upscale.formats, t.common.maxFileSize]}
        collapsed={Boolean(image)}
      />

      <div className={cn('grid grid-cols-1 gap-6', image && 'lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start')}>
        <div className="min-w-0 space-y-6">
          {!image && (
            <Card
              className={cn('mx-auto max-w-3xl transition-colors', dropActive && 'border-primary bg-primary/5')}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <CardContent className="p-4 text-center sm:p-8">
                <div className="mx-auto mb-5 hidden h-16 w-16 place-items-center sm:grid rounded-md border bg-background text-sm font-semibold text-muted-foreground">
                  IMG
                </div>
                <p className="mb-2 hidden text-lg font-medium [@media(pointer:fine)]:block">{t.upscale.dropTitle}</p>
                <p className="mx-auto mb-5 max-w-md text-sm leading-6 text-muted-foreground">
                  {t.upscale.dropDescription}
                </p>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp"
                  className="hidden"
                  onChange={handleInputChange}
                />
                <Button onClick={() => inputRef.current?.click()}>
                  {t.upscale.chooseFile}
                </Button>
              </CardContent>
            </Card>
          )}

          {image && (
            <Card className="overflow-hidden">
              <CardContent className="space-y-4 p-4 sm:p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="truncate text-lg font-semibold tracking-normal" title={image.file.name}>
                      {image.file.name}
                    </h2>
                    <div className="mt-2 hidden flex-wrap gap-2 sm:flex">
                      <span className="rounded-md border bg-background px-2 py-1 text-xs font-medium text-muted-foreground">
                        {image.mime.replace('image/', '').toUpperCase()}
                      </span>
                      <span className="rounded-md border bg-background px-2 py-1 text-xs text-muted-foreground">
                        {formatSize(image.file.size)}
                      </span>
                      <span className="rounded-md border bg-background px-2 py-1 text-xs text-muted-foreground">
                        {image.width} x {image.height}
                      </span>
                    </div>
                  </div>
                  <Button variant="outline" onClick={() => inputRef.current?.click()}>
                    {t.upscale.replaceImage}
                  </Button>
                </div>

                <input
                  ref={inputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp"
                  className="hidden"
                  onChange={handleInputChange}
                />

                <div className="rounded-md border bg-muted/35 px-3 py-2 text-sm leading-6 text-muted-foreground">
                  {t.upscale.qualityNotice}
                </div>

                <div className="grid grid-cols-2 gap-2 sm:gap-4">
                  <PreviewPanel
                    label={t.upscale.before}
                    src={image.url}
                    name={image.file.name}
                    size={formatSize(image.file.size)}
                    dimensions={`${image.width} x ${image.height}`}
                  />
                  <PreviewPanel
                    label={t.upscale.after}
                    src={result?.url ?? image.url}
                    name={result ? outputFilename(image.file, result.mime) : t.upscale.waitingPreview}
                    size={result ? formatSize(result.blob.size) : t.upscale.notReady}
                    dimensions={`${result?.width ?? targetWidth} x ${result?.height ?? targetHeight}`}
                    delta={resultDelta}
                    muted={!result}
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {error && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {error}
            </div>
          )}
        </div>

        {image && (
          <aside className="min-w-0 space-y-4 lg:sticky lg:top-24">
            <Card>
              <CardContent className="space-y-5 p-5">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-lg font-semibold tracking-normal">{t.upscale.settings}</h2>
                  <LocalizedProcessingBadge variant="local" />
                </div>

                <div className="space-y-2">
                  <div className="text-sm font-medium">{t.upscale.presetsTitle}</div>
                  <div className="grid grid-cols-4 overflow-hidden rounded-md border">
                    {t.upscale.presets.map((item) => (
                      <button
                        key={item.key}
                        type="button"
                        className={cn(
                          'px-2 py-2 text-sm font-medium transition-colors',
                          item.key !== '2' && 'border-l',
                          preset === item.key
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-background hover:bg-muted',
                        )}
                        onClick={() => {
                          const presetKey = item.key as UpscalePresetKey
                          if (presetKey === 'custom') {
                            setPreset('custom')
                            return
                          }
                          applyPreset(presetKey)
                        }}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <label className="space-y-2">
                    <span className="text-sm font-medium">{t.upscale.width}</span>
                    <input
                      type="number"
                      min={1}
                      max={maxWidth}
                      step={1}
                      value={targetWidth}
                      onChange={handleWidthChange}
                      className="w-full rounded-md border bg-background px-3 py-2 text-sm tabular-nums outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                  </label>
                  <label className="space-y-2">
                    <span className="text-sm font-medium">{t.upscale.height}</span>
                    <input
                      type="number"
                      min={1}
                      max={maxHeight}
                      step={1}
                      value={targetHeight}
                      onChange={handleHeightChange}
                      className="w-full rounded-md border bg-background px-3 py-2 text-sm tabular-nums outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                  </label>
                </div>

                <label className="flex items-center justify-between gap-3 rounded-md border bg-background px-3 py-2">
                  <span className="text-sm font-medium">{t.upscale.keepAspect}</span>
                  <input
                    type="checkbox"
                    checked={keepAspect}
                    onChange={handleKeepAspectChange}
                    className="h-4 w-4 accent-primary"
                  />
                </label>

                {image.mime !== 'image/png' && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-medium">
                        {image.mime === 'image/webp' ? t.upscale.webpQuality : t.upscale.jpegQuality}
                      </span>
                      <span className="text-sm tabular-nums text-muted-foreground">
                        {quality}%
                      </span>
                    </div>
                    <input
                      type="range"
                      min={60}
                      max={100}
                      step={1}
                      value={quality}
                      onChange={handleQualityChange}
                      className="w-full accent-primary"
                    />
                  </div>
                )}

                <Button
                  className="w-full"
                  size="lg"
                  onClick={handleUpscale}
                  disabled={!canUpscale}
                >
                  {isProcessing ? t.upscale.processing : t.upscale.upscaleButton}
                </Button>

                {result && (
                  <Button className="w-full" variant="outline" onClick={handleDownload}>
                    {t.upscale.download}
                  </Button>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-3 p-5">
                <h2 className="text-lg font-semibold tracking-normal">{t.upscale.summary}</h2>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <div className="flex justify-between gap-3">
                    <span>{t.upscale.originalSize}</span>
                    <strong className="font-medium text-foreground">
                      {formatSize(image.file.size)}
                    </strong>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span>{t.upscale.scaleLabel}</span>
                    <strong className="font-medium text-foreground">{scaleLabel}</strong>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span>{t.upscale.targetSize}</span>
                    <strong className="font-medium text-foreground">
                      {targetWidth} x {targetHeight}
                    </strong>
                  </div>
                  {result && (
                    <div className="flex justify-between gap-3">
                      <span>{t.upscale.resultSize}</span>
                      <strong className="font-medium text-foreground">
                        {formatSize(result.blob.size)}
                      </strong>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <p className="rounded-md border bg-card px-3 py-2 text-xs leading-5 text-muted-foreground">
              {t.upscale.privacyNote}
            </p>
          </aside>
        )}
      </div>

      <section className="mt-12 grid gap-4 border-t pt-8 lg:grid-cols-3">
        {t.upscale.page.faq.map((item) => (
          <div key={item.question}>
            <h2 className="text-base font-semibold tracking-normal">{item.question}</h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              {item.answer}
            </p>
          </div>
        ))}
      </section>
    </div>
  )
}

function PreviewPanel({
  label,
  src,
  name,
  size,
  dimensions,
  delta,
  muted = false,
}: {
  label: string
  src: string
  name: string
  size: string
  dimensions: string
  delta?: number | null
  muted?: boolean
}) {
  return (
    <div className="overflow-hidden rounded-md border bg-background">
      <div className={cn('aspect-video bg-muted', muted && 'opacity-55')}>
        <img src={src} alt={name} className="h-full w-full object-contain" />
      </div>
      <div className="space-y-1 p-2 sm:space-y-3 sm:p-3">
        <div>
          <div className="text-xs text-muted-foreground">{label}</div>
          <div className="truncate text-sm font-medium" title={name}>
            {name}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <span className="rounded-md border bg-card px-2 py-1 text-xs text-muted-foreground">
            {dimensions}
          </span>
          <span className="rounded-md border bg-card px-2 py-1 text-xs text-muted-foreground">
            {size}
            {typeof delta === 'number' && (
              <span
                className={cn(
                  'ml-1 font-medium',
                  delta <= 0 ? 'text-primary' : 'text-muted-foreground',
                )}
              >
                {delta > 0 ? '+' : ''}
                {delta}%
              </span>
            )}
          </span>
        </div>
      </div>
    </div>
  )
}
